// TDU stage-map — assembles parsed route + classified markers into render-ready data:
// route GeoJSON for the Mapbox line layer, and race-feature markers each with cumulative
// distance + ETA. Pure so it's unit-tested independently of Mapbox/React.

import { dedupeConsecutive, totalLengthKm, distanceForEta, nearestDistanceKm, passesForPoint, cumulativeDistancesKm, resample, planarKm, elevationProfile, gradientAtKm, gradientApproachKm, coordAtKm } from './route.js';
import { elapsedHoursSimple, elapsedHoursTerrain, etaClockMinutes, formatClock, etaWindow, formatEtaWindow } from './eta.js';
import { isWeakLabel, TYPE_DISPLAY_NAME, parseAverageGradient } from './classify.js';

/**
 * Give every feature a friendly, distinguishable `displayLabel`. Plotaroute truncates source names
 * to ~10 chars ("King of th", "Feed &", "Ziptrak Sp"), which read as broken AND leave several
 * same-type points indistinguishable. Weak labels (see classify.isWeakLabel) become the friendly
 * type name; then any remaining same-label collision is disambiguated by the (first-pass) race
 * distance, so two sprints read "Sprint · 69 km" / "Sprint · 103 km". A genuine location name is
 * kept as-is. Mutates each feature in place.
 */
export function assignDisplayLabels(features) {
    const friendlyBaseLabel = (f) => (isWeakLabel(f.label, f.type) ? (TYPE_DISPLAY_NAME[f.type] || f.label || f.type) : f.label);
    // Object.create(null): a feature label that happens to equal an Object.prototype key
    // ("toString", "constructor", "hasOwnProperty") must not resolve up the prototype chain when
    // used as a count key, or the tally corrupts. A null-prototype map has no inherited keys.
    const labelCounts = Object.create(null);
    for (const f of features) {
        const key = friendlyBaseLabel(f);
        labelCounts[key] = (labelCounts[key] || 0) + 1;
    }
    for (const f of features) {
        const key = friendlyBaseLabel(f);
        // Only append a distance when it both disambiguates a collision AND is a real number —
        // never render "Sprint · NaN km" if a feature somehow arrives without a race distance.
        const disambiguateByDistance = labelCounts[key] > 1 && Number.isFinite(f.raceDistanceKm);
        f.displayLabel = disambiguateByDistance ? `${key} · ${Math.round(f.raceDistanceKm)} km` : key;
    }
    return features;
}

/**
 * Collapse markers that represent the SAME physical feature appearing more than once in the source.
 * Plotaroute / Google Earth exports routinely mark a circuit feature (KOM, feed, sprint) once per
 * lap, and carry both a generic "START"/"FINISH" and a race-specific "Start Race"/"Race Finish".
 * Left alone, each copy is also geometrically multi-pass-detected, so one feed zone can become a
 * dozen near-identical rows. Keeping ONE per physical feature is correct: passesForPoint re-derives
 * the laps from the route line. Start/Finish are forced to a single instance (they pin to the line
 * endpoints regardless of how many start/finish placemarks exist; the neutral start is preferred).
 * Same-type markers within `radiusKm` of one already kept are dropped — distinct feed zones / KOMs
 * are kilometres apart, so a tight radius can't merge genuinely different features.
 */
export function dedupeFeatures(features, radiusKm = 0.075) {
    const startMarker = features.find((m) => m.type === 'START_NEUTRAL') || features.find((m) => m.type === 'START');
    const finishMarker = features.find((m) => m.type === 'FINISH');
    const kept = [];
    if (startMarker) kept.push(startMarker);
    if (finishMarker) kept.push(finishMarker);
    for (const m of features) {
        if (m === startMarker || m === finishMarker) continue;
        // Any extra start/finish placemark is a duplicate of the single pinned endpoint.
        if (m.type === 'START' || m.type === 'START_NEUTRAL' || m.type === 'FINISH') continue;
        const twin = kept.find((k) => k.type === m.type && planarKm(k.lng, k.lat, m.lng, m.lat) <= radiusKm);
        if (twin) {
            // The source marks a circuit feature once per lap it's active on, so the number of
            // co-located placemarks IS the official lap count (e.g. 2 Ziptrak Sprint placemarks at the
            // Tanunda rotunda = contested on laps 1 & 2, even though the 3-lap route geometrically
            // passes the spot a 3rd time). passesForPoint re-derives the laps from geometry and would
            // over-count, so we keep that count here for selectActivePasses to cap by. Starts at 1
            // (the kept marker itself) and increments for each merged twin.
            twin.lapCount = (twin.lapCount || 1) + 1;
        } else {
            kept.push(m);
        }
    }
    return kept;
}

// A feed/sprint pass whose remaining distance to the finish is below this counts as "at the finish".
const FINISH_COINCIDENT_KM = 0.5;

/**
 * Restrict a repeated (circuit) feature's geometric passes to the laps it is actually contested or
 * active on. Which laps count is race-organiser knowledge, not derivable from the route geometry, so
 * this encodes racing-sound DEFAULTS and yields to an explicit editor override (`marker.activeLaps`,
 * 1-based pass numbers) whenever one is supplied:
 *   - SPRINT : the source places a sprint placemark once per contested lap, so `marker.lapCount`
 *              (the number of co-located source placemarks, set by dedupeFeatures) IS the official
 *              number of contests. A SINGLE placemark therefore means ONE contest — keep only the
 *              earliest pass, so a circuit that merely drives back past the spot on a later lap no
 *              longer shows a phantom 2nd/3rd row (e.g. Lobethal: 1 placemark, passed 2× → lap 1 only).
 *              Two placemarks → laps 1 & 2. Then drop any pass at the finish line (a finishing-circuit
 *              sprint isn't contested on the run-in). The stage-level `sprintLap` still pins one lap.
 *   - KOM    : a KOM is climbed on EVERY lap it's passed, so DON'T collapse a single-placemark KOM
 *              (Checker Hill is one placemark genuinely contested 3×). When the source DID mark it per
 *              lap, honour that count; and never count the finish-line crossing as an ascent — a KOM
 *              whose later pass coincides with the finish (e.g. Stirling Climb, a summit finish) shows
 *              that pass as the finish, not a 2nd KOM. So: keep all passes (capped by lapCount when
 *              the source marks per lap), minus any pass at the finish.
 *   - FEED   : riders feed on the mid-race laps, NOT on the start roll-out or the run-in to the
 *              finish → drop any pass at the start/neutral line and any pass at the finish line.
 *              (On an Angaston-style circuit the start IS the finish, so a feed marker there is
 *              detected on the km-0 crossing — that's not a feed; the real first feed is the next
 *              time they come past, at the end of lap 1.)
 *   - others : keep every pass (vantage every lap; START/FINISH are already single).
 * Never returns empty (falls back to the first pass) so a feature can't silently disappear. Passes
 * keep their original lap numbers, so a feed kept on laps 2–3 still reads "Lap 2" / "Lap 3".
 */
export function selectActivePasses(marker, passes, { finishCoincidentKm = FINISH_COINCIDENT_KM, startCoincidentKm = FINISH_COINCIDENT_KM, sprintLap } = {}) {
    if (passes.length <= 1) return passes;
    // Explicit per-marker override wins over everything (1-based pass numbers).
    const activeLaps = Array.isArray(marker.activeLaps) ? marker.activeLaps : null;
    if (activeLaps && activeLaps.length) {
        const kept = passes.filter((p) => activeLaps.includes(p.pass));
        return kept.length ? kept : [passes[0]];
    }
    if (marker.type === 'END_NEUTRAL') {
        // The race neutral ends exactly once — when racing begins. On a circuit the same point is
        // crossed on every lap, but the flag drops only on the first pass; the later crossings are
        // not "end of race neutral". Collapse to the first pass (an activeLaps override above still
        // wins for the rare stage where racing is neutralised again on a later lap).
        return [passes[0]];
    }
    if (marker.type === 'SPRINT') {
        // A stage-level "Sprint lap" narrows to that one lap (a genuinely single sprint on a multi-pass
        // circuit); an out-of-range value falls back to the first pass.
        const lap = Number(sprintLap);
        if (Number.isInteger(lap) && lap >= 1) {
            const kept = passes.filter((p) => p.pass === lap);
            return kept.length ? kept : [passes[0]];
        }
        // Default: one pass per official contest. The source places the sprint once per contested lap,
        // so marker.lapCount is the official count (Ziptrak #1 & #2 → laps 1 & 2). A single placemark
        // (no merged twin → lapCount undefined) means ONE contest, so cap to 1 and keep the earliest:
        // a later geometric crossing of the same spot is just a drive-through, not a sprint. Then drop
        // any pass that lands on the finish line (a finishing-circuit sprint isn't contested on the run-in).
        const cap = Number.isInteger(Number(marker.lapCount)) ? Number(marker.lapCount) : 1;
        const capped = cap >= 1 && cap < passes.length ? passes.slice(0, cap) : passes;
        const kept = capped.filter((p) => p.toFinishKm > finishCoincidentKm);
        return kept.length ? kept : [capped[0]];
    }
    if (marker.type === 'KOM') {
        // Keep every ascent (don't collapse a single-placemark multi-lap KOM like Checker Hill), but
        // honour a per-lap placemark count when the source supplies one, and never count the finish-line
        // crossing as an ascent — a summit-finish KOM (Stirling) whose later pass coincides with the
        // finish shows that pass as the finish, not a 2nd KOM.
        const cap = Number(marker.lapCount);
        const capped = Number.isInteger(cap) && cap >= 1 && cap < passes.length ? passes.slice(0, cap) : passes;
        const kept = capped.filter((p) => p.toFinishKm > finishCoincidentKm);
        return kept.length ? kept : [capped[0]];
    }
    if (marker.type === 'FEED') {
        const kept = passes.filter((p) => p.toFinishKm > finishCoincidentKm && p.distanceKm > startCoincidentKm);
        return kept.length ? kept : [passes[0]];
    }
    return passes;
}

/**
 * ETA for a cumulative route distance, honouring the neutral rollout (§6.3). When caravanLeadMin
 * / ridersTailMin are supplied, etaLabel becomes the spectator passing window and caravanLabel
 * carries the publicity-caravan time; both default off (single lead-rider ETA, prior behaviour).
 */
export function etaForDistance(cumulativeKm, { neutralEndKm = 0, startMinutes, neutralStartMinutes, avgSpeedKmh, terrain, fmt, caravanLeadMin = 0, ridersTailMin = 0 } = {}) {
    const d = distanceForEta(cumulativeKm, neutralEndKm);
    let etaMinutes;
    if (Number.isFinite(neutralStartMinutes) && Number.isFinite(startMinutes) && neutralEndKm > 0 && cumulativeKm < neutralEndKm) {
        // Inside the neutral rollout the bunch travels at a steady controlled pace, so a point here is
        // reached on the NEUTRAL timeline — interpolate linearly between the flag drop
        // (neutralStartMinutes) and the official start (startMinutes) by how far into the neutral it
        // sits. At km 0 it reads the flag-drop time; at neutralEndKm it meets the official start, so it
        // stays continuous with the post-neutral path. Without this, every in-neutral feature (e.g. a
        // vantage point at the start line) wrongly inherits the official-start time (the reported bug).
        const frac = Math.max(0, cumulativeKm) / neutralEndKm;
        etaMinutes = etaClockMinutes(neutralStartMinutes + frac * (startMinutes - neutralStartMinutes), 0);
    } else {
        const hours = terrain
            ? elapsedHoursTerrain(terrain.segments, terrain)
            : elapsedHoursSimple(d, avgSpeedKmh);
        etaMinutes = etaClockMinutes(startMinutes, hours);
    }
    const window = etaWindow(etaMinutes, { caravanLeadMin, ridersTailMin });
    return {
        distanceForEtaKm: d,
        etaMinutes,
        etaLabel: formatEtaWindow(window, fmt),
        caravanLabel: window.caravanMin != null ? formatClock(window.caravanMin, fmt) : null,
        window,
    };
}

/**
 * Guarantee the two neutral-rollout features the table always needs — Start (spectators watch the
 * roll-out here) and End-of-race-neutral (race km 0, where racing officially begins) — even when a
 * race's route file omits the placemark. A synthesised Start pins to the line start (km 0); a
 * synthesised End-neutral pins to the line point at `neutralEndKm`. No-ops when the marker already
 * exists, and skips End-neutral when there's no neutral (`neutralEndKm` <= 0) or no drawable line.
 */
export function ensureNeutralMarkers(markers, line, neutralEndKm) {
    const hasStart = markers.some((m) => m.isRaceFeature && (m.type === 'START_NEUTRAL' || m.type === 'START'));
    const hasEndNeutral = markers.some((m) => m.isRaceFeature && m.type === 'END_NEUTRAL');
    const extra = [];
    if (!hasStart && line.length) {
        extra.push({ index: -1, lng: line[0][0], lat: line[0][1], type: 'START_NEUTRAL', label: 'Start', isRaceFeature: true, synthetic: true });
    }
    if (!hasEndNeutral && neutralEndKm > 0 && line.length > 1) {
        const c = coordAtKm(line, neutralEndKm);
        if (c) extra.push({ index: -2, lng: c[0], lat: c[1], type: 'END_NEUTRAL', label: 'End Race Neutral', isRaceFeature: true, synthetic: true });
    }
    return extra.length ? [...extra, ...markers] : markers;
}

/**
 * @param {{ line:number[][], markers:Array<object>, neutralEndKm?:number,
 *           startMinutes:number, avgSpeedKmh:number, fmt?:object }} input
 * @returns {{ routeGeoJSON:object, markersGeoJSON:object, features:Array<object>, line:number[][], totalKm:number }}
 */
export function buildStage({ line, markers = [], neutralEndKm = 0, raceLengthKm = 0, startMinutes, neutralStartMinutes, avgSpeedKmh, fmt, caravanLeadMin = 0, ridersTailMin = 0, passThreshKm = 0.12, passDetectSpacingM = 50, dedupeRadiusKm = 0.075, finishCoincidentKm = FINISH_COINCIDENT_KM, sprintLap }) {
    const cleanLine = dedupeConsecutive(line);
    markers = ensureNeutralMarkers(markers, cleanLine, neutralEndKm);
    const totalKm = totalLengthKm(cleanLine);
    // The official racing distance the "km to go" countdown targets. A GPS export is typically a few
    // hundred metres off the published figure (e.g. 145.9 km measured vs 145.6 km official), so when
    // an editor supplies the official race length (EXCLUDING the neutral roll-out) we count down to
    // that; otherwise the measured racing length. Used for display only — ETAs still use the geometry.
    const raceLength = raceLengthKm > 0 ? raceLengthKm : Math.max(0, totalKm - neutralEndKm);
    const profile = elevationProfile(cleanLine);
    // Cumulative distance per raw vertex — retained for back-compat and as the hover fallback
    // (StageMap reads stage.detectCumKm first, stage.cumKm only if absent). The per-segment turf
    // length is too costly to redo per marker / per mousemove.
    const cumKm = cleanLine.length > 1 ? cumulativeDistancesKm(cleanLine) : [0];

    // Pass detection runs on a UNIFORMLY densified copy of the line, not the raw vertices. A point
    // counts as "passed" only when a vertex lands within passThreshKm (120 m) of it, so on a sparsely
    // sampled export — vertices spaced wider than the threshold — a genuine lap is missed silently
    // (a 3-lap circuit could report 2, or fall back to a single off-route snap, with no error).
    // Resampling to ~passDetectSpacingM guarantees a sample inside the band for any roadside point and
    // makes detection independent of the source file's vertex density. The drawn route + elevation
    // profile deliberately keep the original cleanLine (resample is 2D and evenly spaced — it would
    // distort the profile shape and bloat the Mapbox GeoJSON source). The hover lookup reads the same
    // detectLine/detectCumKm so a hovered point and its marker agree on lap count.
    const detect = cleanLine.length > 1 ? resample(cleanLine, passDetectSpacingM) : [];
    const detectLine = detect.map((p) => [p.lng, p.lat]);
    const detectCumKm = detect.map((p) => p.distanceKm);

    const enrichPass = (distanceKm, pass, type, gradientOverride = null) => {
        // The neutral-boundary markers keep their own handling: START is overridden to the flag-drop
        // time below, and END_NEUTRAL must read the official start (its detected km can sit a hair under
        // neutralEndKm, which interpolation would round down off the official time). Every OTHER feature
        // inside the neutral interpolates its time on the roll-out timeline.
        const interpNeutralStart = (type === 'START_NEUTRAL' || type === 'END_NEUTRAL') ? undefined : neutralStartMinutes;
        const eta = etaForDistance(distanceKm, { neutralEndKm, startMinutes, neutralStartMinutes: interpNeutralStart, avgSpeedKmh, fmt, caravanLeadMin, ridersTailMin });
        // Grade priority: the OFFICIAL average gradient from the KML description (gradientOverride)
        // when the export carries it, else computed from SRTM. A KOM sits at the crest, where a
        // centred window averages the climb with the descent over the top and reads ~flat — so for a
        // KOM we measure the approach (the run UP to the summit) rather than a centred window.
        const gradientPct = Number.isFinite(gradientOverride)
            ? gradientOverride
            : (profile.length > 1
                ? (type === 'KOM' ? gradientApproachKm(profile, distanceKm) : gradientAtKm(profile, distanceKm))
                : null);
        // Spectator-facing distances measure from the END of the neutral rollout (race km 0): the
        // neutral section reads 0 km and the race length excludes it. distanceForEtaKm is the
        // neutral-adjusted distance etaForDistance already computed. The finish is pinned to the
        // official race length so it reads exactly e.g. 145.6 km with 0 to go (the GPS line is a touch
        // longer/shorter); intermediate points keep their measured race distance and just count down
        // to the official total. Raw distanceKm is kept for the elevation profile / sorting / geometry,
        // which must stay measured from the line start.
        const raceDistanceKm = type === 'FINISH' ? raceLength : eta.distanceForEtaKm;
        return {
            pass,
            distanceKm,
            toFinishKm: Math.max(0, totalKm - distanceKm),
            raceDistanceKm,
            raceToFinishKm: Math.max(0, raceLength - raceDistanceKm),
            gradientPct,
            ...eta,
        };
    };

    const features = dedupeFeatures(markers.filter((m) => m.isRaceFeature && !m.hidden), dedupeRadiusKm)
        .map((m) => {
            // Resolve EVERY pass, not one. Start/Finish are pinned to the endpoints — correct for
            // point-to-point stages and for a finishing circuit where the line ends at the finish
            // line (nearestPointOnLine would otherwise snap them to an arbitrary mid-route lap).
            // Other features use passesForPoint: a circuit feature passed N× gets N ETAs; an
            // off-line marker (empty result, e.g. a vantage on the neutral spur) falls back to a
            // single nearest snap, flagged offRoute so the UI can suppress a misleading time.
            let passKm;
            let offRoute = false;
            if (m.type === 'START_NEUTRAL' || m.type === 'START') passKm = [0];
            else if (m.type === 'FINISH') passKm = [totalKm];
            else if (detectLine.length > 1) {
                passKm = passesForPoint(detectLine, [m.lng, m.lat], { threshKm: passThreshKm, cumKm: detectCumKm });
                if (passKm.length === 0) { passKm = [nearestDistanceKm(cleanLine, [m.lng, m.lat])]; offRoute = true; }
            } else passKm = [0];

            // A KOM's official average gradient may be published in its KML description; if so it
            // overrides the SRTM-derived grade on every pass (the same climb each lap).
            const officialGrade = m.type === 'KOM' ? parseAverageGradient(m.description) : null;
            const allPasses = passKm.map((d, i) => enrichPass(d, i + 1, m.type, officialGrade));
            // Keep only the laps this feature is actually active on (sprint = first, feed = not the
            // finish lap, editor override wins) so a circuit point doesn't list laps it isn't contested on.
            // A feed at the start/finish line is detected on the km-0 crossing; that's the roll-out,
            // not a feed. Treat anything inside the neutral (and the immediate start) as "at the start".
            const passes = selectActivePasses(m, allPasses, { finishCoincidentKm, startCoincidentKm: Math.max(FINISH_COINCIDENT_KM, neutralEndKm), sprintLap });
            const first = passes[0];
            // The Start (neutral) row shows the flag-drop/neutral time, not the official anchor that
            // every other ETA uses (so a 3 km neutral reads e.g. 10:40 start → 10:45 end of neutral).
            // When the editor has NOT set a neutral start time, render "—" rather than falling through
            // to the official anchor — otherwise the Start and the End-of-Race-Neutral rows show the
            // identical time (the reported bug), which reads as wrong instead of as "fill this in".
            if (m.type === 'START_NEUTRAL' || m.type === 'START') {
                first.etaLabel = Number.isFinite(neutralStartMinutes) ? formatClock(neutralStartMinutes, fmt) : '—';
                first.caravanLabel = null;
            }
            return {
                ...m,
                // Top-level fields mirror the first pass so single-pass consumers/tests are unchanged.
                distanceKm: first.distanceKm,
                toFinishKm: first.toFinishKm,
                raceDistanceKm: first.raceDistanceKm,
                raceToFinishKm: first.raceToFinishKm,
                gradientPct: first.gradientPct,
                etaLabel: first.etaLabel,
                etaMinutes: first.etaMinutes,
                caravanLabel: first.caravanLabel,
                window: first.window,
                passes,
                multiPass: passes.length > 1,
                offRoute,
            };
        })
        .sort((a, b) => a.distanceKm - b.distanceKm);

    // Friendly, distinguishable labels (fixes plotaroute's truncated "King of th" / "Feed &" names).
    assignDisplayLabels(features);

    const routeGeoJSON = {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: cleanLine.map((c) => [c[0], c[1]]) },
    };
    const markersGeoJSON = {
        type: 'FeatureCollection',
        features: features.map((f) => ({
            type: 'Feature',
            properties: { type: f.type, label: f.label, etaLabel: f.etaLabel, distanceKm: f.distanceKm },
            geometry: { type: 'Point', coordinates: [f.lng, f.lat] },
        })),
    };

    const elevations = profile.map((p) => p.elevationM);
    const minEleM = elevations.length ? Math.min(...elevations) : null;
    const maxEleM = elevations.length ? Math.max(...elevations) : null;

    return { routeGeoJSON, markersGeoJSON, features, line: cleanLine, totalKm, profile, minEleM, maxEleM, cumKm, detectLine, detectCumKm };
}
